'use client';

import { useState, useMemo, type ReactNode } from 'react';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { MiniDetailViewDataDocument } from '@packages/gql/generated/shopping/MiniDetailViewDataDocument';
import { MiniDetailViewLayerFragmentFragmentDoc } from '@packages/gql/generated/shopping/MiniDetailViewLayerFragmentFragmentDoc';
import { useQuery } from 'urql';
import { useIntl } from 'react-intl';
import type { DialogProps } from '@packages/shared/src/ui/Dialog/Dialog';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/shared/src/ui/Dialog/DialogHeader/DialogHeader';
import { DialogFooter } from '@packages/shared/src/ui/Dialog/DialogFooter/DialogFooter';
import { getProductAndFallbackId } from '../helpers/getProductAndFallbackId/getProductAndFallbackId';
import { AddToBasketButton, type AddToBasketButtonProps } from '../AddToBasketButton';
import { EditInWishlistButton } from '../EditInWishlistButton/EditInWishlistButton';
import { MiniDVSkeleton } from './MiniDVSkeleton/MiniDVSkeleton';
import { MiniDV } from './MiniDV/MiniDV';
import { miniDVMessages } from './messages';

/* GraphQL */ `
  fragment MiniDetailViewLayerFragment on Product {
    ...MiniDetailViewFragment
    ...AddToBasketFragment
    ...EditInWishlistFragment
  }
`;

/* GraphQL */ `
  query MiniDetailViewData(
    $productId: String!
    $fallbackId: String!
    $locale: String!
    $lang: String!
    $skipAplus: Boolean! = false
  ) {
    product(id: $productId, fallbackId: $fallbackId, locale: $locale) {
      ...MiniDetailViewLayerFragment
      sku
    }
  }
`;

export type MiniDVLayerProps = {
  /**
   * product sku
   */
  sku: string;
  /**
   * product akl
   */
  akl: string;
  /**
   * it needs to update current lineItem in the basket.
   * without lineItemId it will add new product in the the basket
   */
  lineItemId?: string;
  /**
   * quantity of the lineItem
   */
  quantity: number;
  /**
   * service layer from the inspire
   */
  serviceLayer?: ReactNode;
  /**
   * mode for handling the data
   * @default 'ATB' - Add to Basket
   * 'EIW' - Edit in Wishlist
   */
  mode?: 'ATB' | 'EIW';
  open: DialogProps['isOpen'];
} & Pick<DialogProps, 'onClose'> &
  Pick<AddToBasketButtonProps, 'onClick'>;

export const MiniDVLayer = ({
  sku,
  akl,
  lineItemId,
  quantity,
  serviceLayer,
  open,
  onClose,
  onClick: onButtonClick,
  mode = 'ATB',
}: MiniDVLayerProps) => {
  const [selectedVariation, setSelectedVariation] = useState<string | null>(sku);
  const { formatMessage } = useIntl();
  const { locale, language } = useI18n();
  const {
    product: {
      aplus: { enabled: aplusEnabled },
    },
  } = useConfig();

  const [{ data }] = useQuery({
    query: MiniDetailViewDataDocument,
    variables: {
      ...getProductAndFallbackId(akl, selectedVariation!),
      locale,
      lang: language,
      skipAplus: !aplusEnabled,
    },
    context: useMemo(() => ({ suspense: false }), []),
  });

  if (!data?.product) {
    return <MiniDVSkeleton />;
  }
  const product = getFragmentData(MiniDetailViewLayerFragmentFragmentDoc, data.product);

  return (
    <Dialog isOpen={open} onClose={onClose}>
      <DialogHeader onClose={onClose}>{formatMessage(miniDVMessages.title)}</DialogHeader>
      <MiniDV
        data={product}
        setSelectedVariation={setSelectedVariation}
        quantity={quantity}
        serviceLayer={serviceLayer || null}
        hideCounter={mode === 'EIW'}
      />
      <DialogFooter>
        <div className="flex flex-col justify-end">
          {mode === 'EIW' && <EditInWishlistButton data={product} prevSku={sku} />}
          {mode === 'ATB' && (
            <AddToBasketButton
              data={product}
              lineItemId={lineItemId}
              onClose={onClose}
              onClick={onButtonClick}
            />
          )}
        </div>
      </DialogFooter>
    </Dialog>
  );
};
